import React from 'react';
import { Link } from 'gatsby';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import useNavigate from '#/shared/hooks/useNavigate';
import ROUTES from '#/constants/routes';
import GTM_HANDLES from '#/shared/gtm-handles';
import { useGetAlertsQuery } from '#/services/developerApi';
import { SECURITY_LINK } from '#/shared/constants';
import TranslateListButton from '#/shared/components/TranslateListButton';
import { ScreenReaderText } from '#/shared/components/Accessibility';
import * as Styled from './styled';

function NavBar({ closeMapPopup }) {
  const { alertBannerIsVisible, mobileMode } = useSelector(s => s.user);
  const { baseLayer } = useSelector(s => s.map);

  const { matchPath } = useNavigate();
  const isHomePage = matchPath([ROUTES.home]) || matchPath([ROUTES.maplibre]);
  const { data } = useGetAlertsQuery();
  const hasSystemWideAlert = data ? data.hasSystemWideAlert : false;
  const intl = useIntl();

  return (
    <Styled.NavBar
      isHomePage={isHomePage}
      alertsBannerVisible={hasSystemWideAlert && alertBannerIsVisible}
      mobileMode={mobileMode}
      baseLayer={baseLayer}
    >
      {
        // this element doesn't get rendered correctly on the server-side
        // because some it's dynamic styling contains logic that checks if a
        // route for non-SSR page is active, so render it in the client
        typeof window !== 'undefined' && (
          <Styled.InnerNav
            data-testid="styled-inner-nav"
            onClick={closeMapPopup}
          >
            <Styled.NavLogoColumn
              to="/"
              data-gtm={GTM_HANDLES.nav.logoButton}
              $isHomePage={isHomePage}
              title={
                isHomePage
                  ? undefined
                  : intl.formatMessage({ id: 'navbar.logos' })
              }
            >
              <Styled.LogoWrapper>
                {!mobileMode ? <Styled.FullLogo /> : <Styled.LogoIcon />}
              </Styled.LogoWrapper>
              <ScreenReaderText>
                <FormattedMessage id="navbar.home_acc" />
              </ScreenReaderText>
            </Styled.NavLogoColumn>
            <Styled.StripeColumn>
              <Styled.Stripe aria-hidden="true" />
            </Styled.StripeColumn>
            <Styled.SkipNavLink>
              <FormattedMessage id="navbar.skip-nav" />
            </Styled.SkipNavLink>
            <Styled.NavSpacer />

            <Styled.SecurityLink
              href={SECURITY_LINK}
              dataGtm={GTM_HANDLES.map.securityButton}
            >
              <Styled.IconWrapper>
                <Styled.SecurityIcon
                  $mobileMode={mobileMode}
                  aria-hidden="true"
                />
              </Styled.IconWrapper>
              <Styled.LabelWrapper>
                <FormattedMessage id="navbar.security-label" />
              </Styled.LabelWrapper>
            </Styled.SecurityLink>
            <TranslateListButton />
            <Styled.MenuButtonLink
              as={Link}
              to={ROUTES.menu.navigate()}
              $dataGtm={GTM_HANDLES.nav.menuOpenButton}
              aria-haspopup="dialog"
            >
              <Styled.IconWrapper>
                <Styled.MenuIcon aria-hidden="true" />
              </Styled.IconWrapper>
              <Styled.MenuButtonText>
                <FormattedMessage id="navbar.menu" />
              </Styled.MenuButtonText>
            </Styled.MenuButtonLink>
          </Styled.InnerNav>
        )
      }
    </Styled.NavBar>
  );
}

NavBar.propTypes = {
  /** Handler for click on InnerNav element  */
  closeMapPopup: PropTypes.func.isRequired,
};

export default NavBar;
